'use client'

import React, { useCallback, useEffect, useState } from 'react'
import { useRegionId } from '@/shared/hooks/use-region-id'
import { RegionSettingsSection } from '@/features/base/components/admin/RegionSettingsSection'
import type { BaseRegionResponse } from '@/features/base/types/region-response'
import LocationSelector from '@/shared/components/location-selector'
import { unwrapBaseRegionResponse } from '@/features/base/types/region-response'

import { authFetchJson } from '@/shared/lib/auth-fetch'

export function RegionSettingsTab() {
  const { regionId, isLoading: isRegionLoading } = useRegionId()
  const [regionData, setRegionData] = useState<BaseRegionResponse | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const loadRegion = useCallback(async () => {
    if (!regionId) return
    try {
      setIsLoading(true)
      setError(null)
      const res = await authFetchJson<any>(`/regions/${regionId}`)
      if (!res.success) throw new Error(res.message || 'Unable to load region data')
      const region = unwrapBaseRegionResponse(res)
      setRegionData(region ?? null)
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load region')
      setRegionData(null)
    } finally {
      setIsLoading(false)
    }
  }, [regionId])

  useEffect(() => {
    if (regionId) loadRegion()
    else setRegionData(null)
  }, [regionId, loadRegion])

  const isBusy = isRegionLoading || isLoading

  return (
    <div className="flex flex-col gap-6">
      <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
        <h3 className="text-lg font-semibold">Region Settings</h3>
        <LocationSelector />
      </div>

      {isBusy && (
        <div className="flex justify-center py-16">
          <div className="h-8 w-8 animate-spin rounded-full border-b-2 border-primary" />
        </div>
      )}

      {!isBusy && !regionId && (
        <div className="rounded-md border border-border p-6 text-center text-muted-foreground">
          Select a region to configure default capacity and week start day.
        </div>
      )}

      {!isBusy && regionId && error && (
        <div className="rounded-md border border-destructive/40 bg-destructive/5 p-6 text-center text-destructive">
          {error}
        </div>
      )}

      {!isBusy && regionId && regionData && (
        <RegionSettingsSection
          region={regionData}
          regionId={regionId}
          onSaved={loadRegion}
        />
      )}
    </div>
  )
}
